import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCheck, FiX, FiEye } from 'react-icons/fi';
import { where, orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Input,
  Pagination,
  CsvExportButton,
  BulkActionBar,
} from '../../components/UI';
import { useFirestoreCollection, usePagination } from '../../hooks';
import { getVetById, approveVet, rejectVet, suspendEntity, unsuspendEntity, reactivateEntity } from '../../services/adminService';
import EditEntityModal from '../../components/admin/EditEntityModal';
import styles from './Vets.module.css';

// Vets exist in TWO places (verified against production, 2026-08-21):
//
//   veterinarians/{vetId} — standalone vet registrations.
//     { userId, verificationStatus, isVerified, firstName, lastName, email,
//       phone, licenseNumber, licenseState, clinicName, clinicAddress,
//       specializations, createdAt }
//
//   businesses/{businessId}.vetServices.veterinarians[] — vets added to an
//     existing business via AddVetToBusinessScreen.
//     { id, firstName, lastName, title, photo, licenseNumber, specializations,
//       yearsOfExperience, verificationStatus, isActive, addedAt }
//
// This page only read the first, and the `veterinarians` collection is empty in
// production — so the tab was permanently blank while real vets sat at
// verificationStatus 'pending' with no way to approve them. Both sources are
// merged below; `source` / `businessId` mark where each row came from so the
// approve/reject callables can act on the right shape.
// Status values: 'pending' | 'approved' | 'rejected'
const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const vetDisplayName = (v) =>
  [v.firstName, v.lastName].filter(Boolean).join(' ') ||
  v.name ||
  v.email ||
  'Dr. Unknown';

const Vets = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'pending');
  const [selectedVet, setSelectedVet] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(() => {
    const c = [];
    if (statusFilter === 'suspended') {
      c.push(where('isSuspended', '==', true));
    } else if (statusFilter !== 'all') {
      c.push(where('verificationStatus', '==', statusFilter));
    }
    c.push(orderBy('createdAt', 'desc'));
    c.push(limit(500));
    return c;
  }, [statusFilter]);
  // Standalone vets. Status filtering happens server-side via `constraints`.
  const { data: standaloneVets, loading: loadingStandalone } =
    useFirestoreCollection('veterinarians', constraints);

  // Business-embedded vets. Filtering must happen client-side: they are array
  // elements inside a business document, not queryable rows.
  const businessConstraints = useMemo(() => [limit(500)], []);
  const { data: businesses, loading: loadingBusinesses } =
    useFirestoreCollection('businesses', businessConstraints);

  const embeddedVets = useMemo(() => {
    const out = [];
    businesses.forEach((b) => {
      const list = b.vetServices && Array.isArray(b.vetServices.veterinarians)
        ? b.vetServices.veterinarians
        : [];
      list.forEach((v, i) => {
        // Some early records have neither `id` nor `verificationStatus`; index
        // keeps the React key unique and 'pending' is the safe default (an
        // unreviewed vet must not be treated as approved).
        out.push({
          ...v,
          id: v.id || v.vetId || `${b.id}#${i}`,
          source: 'business',
          businessId: b.id,
          businessName: b.businessName,
          clinicName: v.clinicName || b.vetServices?.clinicName || b.businessName,
          email: v.email || b.email,
          phone: v.phone || b.phone,
          photoURL: v.photoURL || v.photo || null,
          verificationStatus: v.verificationStatus || 'pending',
          createdAt: v.addedAt || b.createdAt,
          // A vet with no stable id cannot be addressed by the callable.
          unaddressable: !(v.id || v.vetId),
        });
      });
    });
    return out;
  }, [businesses]);

  const loading = loadingStandalone || loadingBusinesses;

  const vets = useMemo(() => {
    const standalone = standaloneVets.map((v) => ({ ...v, source: 'standalone' }));
    const embedded = statusFilter === 'all'
      ? embeddedVets
      : statusFilter === 'suspended'
        ? embeddedVets.filter((v) => v.isSuspended === true)
        : embeddedVets.filter((v) => v.verificationStatus === statusFilter);
    return [...standalone, ...embedded];
  }, [standaloneVets, embeddedVets, statusFilter]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(vets, 25);

  useEffect(() => {
    if (statusFilter !== 'pending') setSearchParams({ status: statusFilter });
    else setSearchParams({});
  }, [statusFilter, setSearchParams]);

  const openVetDetail = async (vet) => {
    setErrorMsg('');
    // Embedded vets have no `veterinarians` document to re-fetch — the row
    // already holds everything the business stores about them.
    if (vet.source === 'business') {
      setSelectedVet(vet);
      setShowDetailModal(true);
      return;
    }
    try {
      const fullVet = await getVetById(vet.id);
      setSelectedVet({ ...vet, ...fullVet });
      setShowDetailModal(true);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Failed to load vet: ${err.message}`);
    }
  };

  const handleApprove = async (vet) => {
    if (vet.unaddressable) {
      setErrorMsg(
        `"${vetDisplayName(vet)}" has no id on the business record, so it cannot be approved from here. ` +
        `Re-submit the vet from the business account to give it one.`
      );
      return;
    }
    try {
      setActionLoading(true);
      await approveVet(vet.id, vet.businessId);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Approve failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedVet || !rejectReason) return;
    try {
      setActionLoading(true);
      await rejectVet(selectedVet.id, rejectReason, selectedVet.businessId);
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedVet(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Reject failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedVet) return;
    const name = `${selectedVet.firstName || ''} ${selectedVet.lastName || ''}`.trim() || selectedVet.clinicName || 'this vet';
    if (!window.confirm(`Suspend ${name}? They won't appear to users until unsuspended.`)) return;
    try {
      setActionLoading(true);
      await suspendEntity({
        entityType: 'vet',
        entityId: selectedVet.id,
        reason: 'Suspended by admin',
      });
      setShowDetailModal(false);
      setSelectedVet(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Suspend failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!selectedVet) return;
    const name = `${selectedVet.firstName || ''} ${selectedVet.lastName || ''}`.trim() || selectedVet.clinicName || 'this vet';
    if (!window.confirm(`Reinstate ${name}?`)) return;
    try {
      setActionLoading(true);
      await unsuspendEntity({
        entityType: 'vet',
        entityId: selectedVet.id,
      });
      setShowDetailModal(false);
      setSelectedVet(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Unsuspend failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedVet) return;
    if (!window.confirm(`Reactivate this rejected vet back to pending? They can resubmit / be reapproved.`)) return;
    try {
      setActionLoading(true);
      await reactivateEntity({ entityType: 'vet', entityId: selectedVet.id });
      setShowDetailModal(false);
      setSelectedVet(null);
    } catch (err) {
      setErrorMsg(`Reactivate failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  };
  const toggleAllOnPage = (checked) => {
    const ids = pageData.map((v) => v.id);
    setSelectedIds((prev) => {
      if (checked) return Array.from(new Set([...prev, ...ids]));
      const drop = new Set(ids);
      return prev.filter((x) => !drop.has(x));
    });
  };
  const clearSelection = () => setSelectedIds([]);

  const bulkApprove = async () => {
    if (!window.confirm(`Approve ${selectedIds.length} vets?`)) return;
    try {
      setActionLoading(true);
      // Bulk actions address vets by id only, so the businessId hint has to be
      // looked up from the merged list — otherwise the callable has to scan.
      await Promise.all(
        selectedIds.map((id) => approveVet(id, vets.find((v) => v.id === id)?.businessId))
      );
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk approve failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const bulkReject = async () => {
    const reason = window.prompt('Reason for rejecting selected vets:');
    if (!reason) return;
    try {
      setActionLoading(true);
      await Promise.all(
        selectedIds.map((id) => rejectVet(id, reason, vets.find((v) => v.id === id)?.businessId))
      );
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk reject failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = { pending: 'warning', approved: 'success', verified: 'success', rejected: 'danger' };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'unknown'}</Badge>;
  };

  const csvColumns = [
    { key: 'id', label: 'Vet ID' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'licenseNumber', label: 'License Number' },
    { key: 'licenseState', label: 'License State' },
    { key: 'clinicName', label: 'Clinic' },
    { key: 'source', label: 'Record type', accessor: (v) => (v.source === 'business' ? 'business-embedded' : 'standalone') },
    { key: 'businessName', label: 'Business' },
    { key: 'verificationStatus', label: 'Status' },
    { key: 'specializations', label: 'Specializations', accessor: (v) => (v.specializations || []).join('; ') },
    { key: 'createdAt', label: 'Applied', accessor: (v) => formatDate(v.createdAt) },
    { key: 'verifiedAt', label: 'Verified At', accessor: (v) => formatDate(v.verifiedAt) },
    { key: 'rejectionReason', label: 'Rejection Reason' },
  ];

  const columns = [
    {
      key: 'name',
      title: 'Veterinarian',
      render: (_, row) => (
        <div className={styles.vetCell}>
          <div className={styles.avatar}>
            {row.photoURL ? (
              <img src={row.photoURL} alt="" />
            ) : (
              <span>{vetDisplayName(row).charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className={styles.vetInfo}>
            <span className={styles.vetName}>{vetDisplayName(row)}</span>
            <span className={styles.vetSpecialty}>
              {(row.specializations && row.specializations.join(', ')) ||
                row.specialization ||
                'General Practice'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'clinic',
      title: 'Clinic',
      render: (_, row) => (
        <div>
          <div>{row.clinicName || row.clinic || '-'}</div>
          {row.source === 'business' && (
            <div className={styles.sourceMeta}>
              via {row.businessName || row.businessId}
              {row.unaddressable ? ' · no id on record' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'verificationStatus',
      title: 'Status',
      render: (value) => getStatusBadge(value),
    },
    { key: 'createdAt', title: 'Applied', render: (value) => formatDate(value) },
    {
      key: 'actions',
      title: '',
      width: '210px',
      render: (_, row) => (
        <div className={styles.actions}>
          <Button variant="ghost" size="sm" icon={<FiEye />} onClick={() => openVetDetail(row)} />
          {row.verificationStatus === 'pending' && (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={<FiCheck />}
                onClick={() => handleApprove(row)}
                disabled={actionLoading}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={<FiX />}
                onClick={() => {
                  setSelectedVet(row);
                  setShowRejectModal(true);
                }}
                disabled={actionLoading}
              >
                Reject
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Veterinarian Applications</h1>
          {!loading && <Badge variant="primary">{vets.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Review and verify veterinarian profiles
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton
            rows={vets}
            columns={csvColumns}
            filename={`dierend-vets-${statusFilter}`}
          />
        </div>

        <BulkActionBar selectedCount={selectedIds.length} onClear={clearSelection}>
          <Button variant="primary" size="sm" onClick={bulkApprove} disabled={actionLoading}>
            Approve Selected
          </Button>
          <Button variant="danger" size="sm" onClick={bulkReject} disabled={actionLoading}>
            Reject Selected
          </Button>
        </BulkActionBar>

        <Table
          columns={columns}
          data={pageData}
          loading={loading}
          emptyMessage="No veterinarian applications found"
          selectable
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAllOnPage}
        />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Veterinarian Details"
        size="lg"
        footer={
          selectedVet?.verificationStatus === 'pending' ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowDetailModal(false);
                  setShowRejectModal(true);
                }}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  handleApprove(selectedVet);
                  setShowDetailModal(false);
                }}
                loading={actionLoading}
              >
                Approve
              </Button>
            </>
          ) : selectedVet?.isSuspended ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button variant="ghost" onClick={() => setShowEditModal(true)}>
                Edit profile
              </Button>
              <Button variant="primary" loading={actionLoading} onClick={handleUnsuspend}>
                Unsuspend
              </Button>
            </>
          ) : (selectedVet?.verificationStatus === 'approved' || selectedVet?.verificationStatus === 'verified') ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button variant="ghost" onClick={() => setShowEditModal(true)}>
                Edit profile
              </Button>
              <Button variant="danger" loading={actionLoading} onClick={handleSuspend}>
                Suspend
              </Button>
            </>
          ) : selectedVet?.verificationStatus === 'rejected' ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>Close</Button>
              <Button variant="primary" loading={actionLoading} onClick={handleReactivate}>
                Reactivate (back to pending)
              </Button>
            </>
          ) : (
            // any other unrecognized status — Close-only fallback.
            <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          )
        }
      >
        {selectedVet && (
          <div className={styles.detailContent}>
            <div className={styles.detailHeader}>
              <div className={styles.avatarLarge}>
                {selectedVet.photoURL ? (
                  <img src={selectedVet.photoURL} alt="" />
                ) : (
                  <span>{vetDisplayName(selectedVet).charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div>
                <h3>Dr. {vetDisplayName(selectedVet)}</h3>
                <p>
                  {(selectedVet.specializations && selectedVet.specializations.join(', ')) ||
                    selectedVet.specialization ||
                    'General Practice'}
                </p>
                {getStatusBadge(selectedVet.verificationStatus)}
              </div>
            </div>

            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>User UID</span>
                <span className={styles.detailValue}>{selectedVet.userId || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Email</span>
                <span className={styles.detailValue}>{selectedVet.email || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Phone</span>
                <span className={styles.detailValue}>{selectedVet.phone || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>License</span>
                <span className={styles.detailValue}>
                  {selectedVet.licenseNumber || '-'}
                  {selectedVet.licenseState ? ` (${selectedVet.licenseState})` : ''}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Clinic</span>
                <span className={styles.detailValue}>{selectedVet.clinicName || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Clinic Address</span>
                <span className={styles.detailValue}>
                  {typeof selectedVet.clinicAddress === 'string'
                    ? selectedVet.clinicAddress
                    : selectedVet.clinicAddress?.street
                    ? `${selectedVet.clinicAddress.street}, ${selectedVet.clinicAddress.city || ''}`
                    : '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Applied</span>
                <span className={styles.detailValue}>{formatDate(selectedVet.createdAt)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Verified</span>
                <span className={styles.detailValue}>{formatDate(selectedVet.verifiedAt)}</span>
              </div>
            </div>

            {selectedVet.bio && (
              <div className={styles.bio}>
                <h4>Bio</h4>
                <p>{selectedVet.bio}</p>
              </div>
            )}

            {Array.isArray(selectedVet.documents) && selectedVet.documents.length > 0 && (
              <div className={styles.bio}>
                <h4>License & Documents</h4>
                <ul>
                  {selectedVet.documents.map((d, i) => (
                    <li key={i}>
                      <a href={d.url || d} target="_blank" rel="noreferrer">
                        {d.name || d.url || `Document ${i + 1}`}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedVet.rejectionReason && (
              <div className={styles.rejectInfo}>
                <h4>Rejection Reason</h4>
                <p>{selectedVet.rejectionReason}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        title="Reject Veterinarian Application"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject} loading={actionLoading} disabled={!rejectReason}>
              Reject
            </Button>
          </>
        }
      >
        <p className={styles.rejectText}>
          You are about to reject <strong>Dr. {vetDisplayName(selectedVet || {})}</strong>.
          Please provide a reason:
        </p>
        <Input
          placeholder="Enter rejection reason..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      <EditEntityModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        entityType="vet"
        entity={selectedVet}
        onSaved={() => {
          if (selectedVet?.id) {
            getVetById(selectedVet.id).then((v) => v && setSelectedVet(v));
          }
        }}
      />
    </div>
  );
};

export default Vets;
